import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import elPortonImg from "@/assets/case-studies/el-porton.png";
import foodBayanaImg from "@/assets/case-studies/food-bayana.webp";
import skylarkGroupImg from "@/assets/case-studies/skylark-group.webp";
import smileParkImg from "@/assets/case-studies/smile-park.webp";
import lithiumBatteryImg from "@/assets/case-studies/lithium-battery.webp";
import mediaMarktImg from "@/assets/case-studies/media-markt.webp";
import xtremeImg from "@/assets/case-studies/xtreme.webp";
import parkhotelImg from "@/assets/case-studies/parkhotel.webp";
import meritumImg from "@/assets/case-studies/meritum.webp";
import mnactecImg from "@/assets/case-studies/mnactec.webp";

const CaseStudies = () => {
  const caseStudies = [
    {
      title: "Serving Up Success: How El Portón Elevated Dining with Service Robotics",
      image: elPortonImg,
      location: "USA",
      industry: "Food & Beverage",
      robots: ["BellaBot", "KettyBot Pro"],
      description: "In Roswell, Georgia, El Portón Mexican Restaurant has been a beloved local destination, known for its sizzling fajitas, inviting atmosphere, and exceptional service. However, behind the scenes, owner Jose Perez faced an increasing challenge within the food and beverage industry: the difficulty of recruiting and retaining reliable staff to uphold their high service standards.",
      solution: "As turnover rates rose and operational demands intensified, Jose sought a solution to support his team without sacrificing the cherished guest experience. This led him to partner with ToDo Robotics, which introduced Pudu Robotics' KettyBot and BellaBot to help El Portón turn the corner in terms of efficiency, morale, and service excellence."
    },
    {
      title: "Revolutionizing Restaurant Service at Skylark Group with BellaBot",
      image: skylarkGroupImg,
      location: "Japan",
      industry: "Food & Beverage",
      robots: ["BellaBot"],
      description: "Skylark Group, a top family restaurant chain in Japan, has transformed its customer service and employee experience by deploying Pudu Robotics' delivery and advertising robot, Bellabot. In an ambitious plan, Skylark introduced 3000 BellaBots across over 2,000 stores in Japan to enhance operational efficiency, alleviate employee workload, and increase customer satisfaction.",
      solution: "An initial survey conducted at eight stores showed that 67% of customers were satisfied with the robots' presence, indicating the popularity of Bellabot's interactive and friendly design."
    },
    {
      title: "FlashBot Revolutionizes Dining Experience at Food Bayana, Malaysia",
      image: foodBayanaImg,
      location: "Malaysia",
      industry: "Food & Beverage",
      robots: ["FlashBot Max"],
      description: "The newly established Food Bayana dining center in Bayan Lepas, Penang, Malaysia, has announced a collaboration with Pudu Robotics and Pentamaster to drive digital transformation within the center.",
      solution: "This partnership signifies a significant step for Food Bayana in integrating automation technology with culinary experiences, aiming to deliver an unprecedented dining experience for customers."
    },
    {
      title: "BellaBot Enhances Efficiency at Smile Park Elderly Care Home",
      image: smileParkImg,
      location: "Japan",
      industry: "Health Care",
      robots: ["BellaBot"],
      description: "At Smile Park Elderly Care Home in Japan, the introduction of BellaBot has effectively addressed the challenges faced by staff in transporting items, significantly improving workflow and service quality.",
      solution: "This innovation not only enhances staff efficiency but also adds an element of fun to the interactions between residents and robots, allowing caregivers to focus more on providing care and improving the overall living experience."
    },
    {
      title: "Tri-Robot Team: Collaborative Delivery & Cleaning in Battery Manufacturing",
      image: lithiumBatteryImg,
      location: "China",
      industry: "Industrial Facility",
      robots: ["PuduBot 2", "PUDU CC1", "PUDU T300"],
      description: "In the era of Industry 4.0, smart manufacturing has become essential for modern factory development. A leading lithium-ion battery manufacturer in China has embraced this trend by implementing innovative solutions from Pudu Robotics.",
      solution: "This multi-robot ecosystem including PuduBot 2, PUDU CC1, and PUDU T300 enhances operational efficiency across various production processes, establishing a new standard for smart manufacturing."
    },
    {
      title: "Media Markt's Robotic Staff: Setting a New Standard for Retail Efficiency",
      image: mediaMarktImg,
      location: "Netherlands",
      industry: "Retail",
      robots: ["BellaBot", "KettyBot Pro"],
      description: "Established in 1979, Media Markt is a German multinational consumer electronics retail chain with over 1,000 stores worldwide and a sales revenue of €21 billion in 2020. With its spacious stores and diverse product range, Media Markt sought to integrate robots to assist customers.",
      solution: "KettyBot's product search functionality effectively met this need, allowing staff to pre-set product and location information, enabling the robot to guide customers throughout the store. The multi-layer trays and advertising screens provided excellent platforms for marketing materials."
    },
    {
      title: "Xtreme & Pudu Robotics Create a New Trend in Sports Entertainment",
      image: xtremeImg,
      location: "Canada",
      industry: "Entertainment and Sports",
      robots: ["KettyBot Pro", "PUDU CC1"],
      description: "Xtreme, a trampoline park in Canada aimed at teens, has introduced Pudu Robotics' KettyBot and PUDU CC1 to enhance operational efficiency and provide a more engaging experience for children.",
      solution: "KettyBot is responsible for delivering food during party events, while PUDU CC1 takes care of cleaning duties within the venue. Both robots have become indispensable and reliable assistants, known for their outstanding efficiency and friendly, interactive features."
    },
    {
      title: "Pudu Robots Enhances Service Efficiency at Parkhotel in Austria",
      image: parkhotelImg,
      location: "Austria",
      industry: "Hospitality",
      robots: ["BellaBot", "KettyBot Pro", "HolaBot", "FlashBot Max", "PUDU CC1"],
      description: "Parkhotel Eisenstadt in Burgenland, Austria, welcomed five special new team members: Pudu Robotics' KettyBot, HolaBot, BellaBot, FlashBot, and the cleaning robot PUDU CC1.",
      solution: "These robots represent the first robotic employees in Burgenland, taking on responsibilities such as floor cleaning, item and food delivery, and serving as welcoming assistants to engage with guests."
    },
    {
      title: "Transforming Workplace Efficiency: Meritum Integrates KettyBot",
      image: meritumImg,
      location: "Poland",
      industry: "Residential and Office Buildings",
      robots: ["KettyBot Pro"],
      description: "Meritum, a Polish company specializing in accounting and payroll services, has introduced Pudu Robotics' KettyBot to revolutionize its reception processes.",
      solution: "This innovative solution greatly enhances operational efficiency and creates a friendly, vibrant workplace atmosphere."
    },
    {
      title: "Interactive Museum Navigation with KettyBot at MNACTEC",
      image: mnactecImg,
      location: "Spain",
      industry: "Public Service",
      robots: ["KettyBot Pro"],
      description: "The National Museum of Science and Technology of Catalonia (MNACTEC) has successfully integrated two KettyBots into its exhibition areas to enhance the visitor experience.",
      solution: "These state-of-the-art guide robots not only assist in navigating the museum but also engage visitors with interactive audiovisual content."
    }
  ];

  return (
    <>
      <SEO 
        title="Case Studies - Real Success Stories | Neil AI"
        description="See how businesses worldwide are transforming operations with PUDU Robotics. Real customer success stories from restaurants, hotels, healthcare, retail, and industrial facilities."
        keywords="robot case studies, PUDU success stories, restaurant automation, hotel robots, healthcare automation, retail robots"
      />
      <Navigation />
      
      <div 
        className="min-h-screen py-8 px-4"
        style={{
          background: `radial-gradient(900px 600px at 10% 10%, #132552 0%, #0B1736 45%, #060f24 100%)`
        }}
      >
        <main className="max-w-7xl mx-auto">
          <Link to="/">
            <Button variant="ghost" className="mb-6 mt-2">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>

          <div className="border border-[hsl(var(--line))] rounded-[14px] p-4 sm:p-6 md:p-12" style={{ boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)" }}>
            <h1 className="font-orbitron font-bold text-3xl sm:text-4xl md:text-5xl mb-6 leading-tight">
              Case <span className="text-primary">Studies</span>
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground mb-10 md:mb-12 max-w-3xl leading-relaxed">
              PUDU Robotics offers flexible delivery and cleaning solutions that effectively tackle real-world challenges, adding value in the F&B, hospitality, healthcare, entertainment, industrial manufacturing sectors, and others.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {caseStudies.map((study, index) => (
                <Card 
                  key={index}
                  className="border-[hsl(var(--line))] glass-effect overflow-hidden hover:scale-[1.02] transition-all duration-300"
                >
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={study.image} 
                      alt={study.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2 mb-3">
                      <Badge variant="secondary" className="text-xs">
                        {study.location}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {study.industry}
                      </Badge>
                    </div>

                    <h3 className="font-orbitron font-bold text-lg mb-3 line-clamp-2">
                      {study.title}
                    </h3>

                    <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                      {study.description}
                    </p>

                    <div className="mb-4">
                      <p className="text-xs font-semibold text-primary mb-2">Robots Deployed:</p>
                      <div className="flex flex-wrap gap-1">
                        {study.robots.map((robot, idx) => (
                          <span 
                            key={idx}
                            className="text-xs bg-primary/10 text-primary px-2 py-1 rounded"
                          >
                            {robot}
                          </span>
                        ))}
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground italic border-l-2 border-primary pl-3">
                      {study.solution}
                    </p>
                  </div>
                </Card>
              ))}
            </div>

            <div className="text-center pt-10 md:pt-12 border-t border-[hsl(var(--line))]">
              <h2 className="font-orbitron font-bold text-2xl sm:text-3xl mb-4 leading-tight">
                Ready to Write Your <span className="text-primary">Success Story?</span>
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground mb-6 max-w-2xl mx-auto leading-relaxed">
                Join hundreds of businesses worldwide transforming their operations with PUDU Robotics and Neil AI automation solutions.
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Button 
                  asChild
                  size="lg"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 font-extrabold rounded-xl"
                >
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Schedule Your Demo
                  </a>
                </Button>
                <Button 
                  asChild
                  size="lg"
                  variant="outline"
                  className="glass-effect border-[hsl(var(--line))] hover:bg-white/10 font-bold rounded-xl"
                >
                  <Link to="/robots">
                    Explore Our Robots
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <Footer />
    </>
  );
};

export default CaseStudies;
